import type { Maybe } from '@adornis/base/utilTypes.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { format, parse } from 'date-fns';

export function fillInEmpty(data: Array<{ year: number; week: number; total: number }>) {
  const registrationsOverTime = [] as Array<{ year: number; week: number; total: number }>;

  for (const { year, week, total } of data.reverse()) {
    const last = registrationsOverTime.at(-1);
    if (last) {
      const { year: lastYear, week: lastWeek } = last;
      if (year === lastYear) {
        // fill in empty
        for (let fillerWeek = lastWeek + 1; fillerWeek < week; fillerWeek++) {
          registrationsOverTime.push({ year, week: fillerWeek, total: 0 });
        }
      }
    }
    registrationsOverTime.push({ year, week, total });
  }

  return registrationsOverTime;
}

export function isDefined<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

/** Converts a Date to a new Date representing the same instant in Europe/Berlin local time,
 * so that date-fns format() (which uses the system timezone) produces Europe/Berlin output. */
export function toBerlinDate(date: Date): Date {
  return new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
}

export function formatBerlinDate(date: Date | null | undefined, pattern: string): string | null {
  return date == null ? null : format(toBerlinDate(date), pattern);
}

/** Inverse of toBerlinDate: takes a Date whose local fields hold a Europe/Berlin wall-clock time
 * (e.g. the result of date-fns parse()) and returns the actual instant. */
export function fromBerlinDate(date: Date): Date {
  const offsetAt = (d: Date) => toBerlinDate(d).getTime() - Math.floor(d.getTime() / 1000) * 1000;
  const guess = new Date(date.getTime() - offsetAt(date));
  // re-check the offset at the guessed instant so dates right around a DST switch come out right
  return new Date(date.getTime() - offsetAt(guess));
}

/** Parses a Europe/Berlin date/time from an import file. Accepts `dd.MM.yy` and `dd.MM.yyyy`, each
 * optionally followed by ` HH:mm`, or ISO-8601 strings; rejects other nonempty values. */
export function parseBerlinDateTime(value: Maybe<string>): Date | null {
  const str = value?.trim();
  if (!str) return null;
  const match = /^\d{1,2}\.\d{1,2}\.(\d{2}|\d{4})( \d{1,2}:\d{2})?$/.exec(str);
  if (!match) {
    if (!/^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(str))
      throw new Error(`Invalid date/time: ${str}`);
    const date = new Date(str);
    if (isNaN(date.getTime())) throw new Error(`Invalid date/time: ${str}`);
    return date;
  }
  // the year length picks the format: 'yyyy' would happily read "26" as the year 26
  const pattern = (match[1]!.length === 2 ? 'd.M.yy' : 'd.M.yyyy') + (match[2] ? ' H:mm' : '');
  const parsed = parse(str, pattern, new Date());
  if (isNaN(parsed.getTime())) throw new Error(`Invalid date/time: ${str}`);
  return fromBerlinDate(parsed);
}

export function at<Tarr extends Array<any>>(index: number) {
  return (arr: Tarr) => arr[index] as Tarr extends Array<infer T> ? T : never;
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function filterObject<T extends { [key: string | number | symbol]: any }>(
  obj: T,
  predicate: (key: string, value: T[keyof T]) => boolean,
) {
  return Object.fromEntries(Object.entries(obj).filter(([key, value]) => predicate(key, value))) as T;
}

let _mailer: AdornisMailer | null = null;

// ? This could be expanded to set mor internal state thats defined in the project scope. Rename this function "configure" or something then
export const setMailer = (mailer: AdornisMailer) => {
  _mailer = mailer;
};

export const getMailer = () => {
  if (!_mailer) throw new Error('no _mailer set!');
  return _mailer;
};

// mongo
// Add a unique tiebreaker so skip/limit pagination is stable when sorted fields tie.
export function withIdTiebreaker(
  sort: Record<string, 1 | -1> | null | undefined,
  idField = '_id',
): Record<string, 1 | -1> {
  const result = { ...sort };
  if (!Object.prototype.hasOwnProperty.call(result, idField)) result[idField] = 1;
  return result;
}

export function lookupEntityById<Tentity extends typeof MongoEntity>(
  Entity: Tentity,
  localField: string,
  name: string,
  project?: BaseQLSelectionSet<Tentity>,
) {
  if (!project)
    return {
      from: Entity._collectionName,
      localField,
      foreignField: '_id',
      as: name,
    };

  return {
    from: Entity._collectionName,
    let: { localField: '$' + localField },
    as: name,
    pipeline: [
      { $match: expr_eq('$_id', '$$localField') }, //
      { $project: project },
    ],
  };
}

export const entityPipeline =
  (base: { from: typeof MongoEntity; as: string }) => (vars: Record<string, any>) => (pipeline: Array<any>) => ({
    from: base.from._collectionName,
    let: vars,
    as: base.as,
    pipeline,
  });

export const expr_eq = (key: string, value: any) => ({ $expr: { $eq: [key, value] } });
export const expr_in = (key: string, values: any[] | string) => ({ $expr: { $in: [key, values] } });
export const expr_and = (...exprs: any[]) => ({ $expr: { $and: exprs } });
export const expr_or = (...exprs: any[]) => ({ $expr: { $or: exprs } });
export const m_and = (...exprs: any[]) => ({ $and: exprs });
export const m_or = (...exprs: any[]) => ({ $or: exprs });

export const jsFun = (fn, ...args) => ({
  $function: {
    body: fn.toString(),
    args,
    lang: 'js',
  },
});

export const searchMatch = (fields: string[], search: Maybe<string>) =>
  search
    ? {
        $expr: {
          $or: fields.map(field => ({ $regexMatch: { input: '$' + field, regex: search, options: 'i' } })),
        },
      }
    : {};

export const withDefault = (value, def) => ({ $ifNull: [value, def] });
